import { Pagination } from '@purinanbm/pds-ui';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { useContext } from 'react';
import { SearchListingContext } from '../search_listing/SearchListingContext';

interface SearchPagerProps {
  pager?: SearchPager;
  containerRef?: React.RefObject<HTMLElement>;
}

const Pager: React.FC<SearchPagerProps> = function ({ pager, containerRef }) {
  const context = useContext(SearchListingContext);
  const { t } = useTranslation();
  const location = useLocation();

  if (!pager || pager?.total_pages === 1) {
    return null;
  }

  const isSSR = typeof window === 'undefined';

  const generateHref = (page: number) => {
    if (isSSR) {
      // Return query string with only page number for crawlers.
      const queryParams = new URLSearchParams();
      queryParams.set('page', `${page}`);
      return `?${queryParams.toString()}`;
    }

    const url = new URL(location.href);
    url.searchParams.set('page', `${page}`);
    return url.pathname + url.search + url.hash;
  };

  const handleAnalyticData = (page: number) => {
    context?.setLoaded(false);

    const analyticsData = {
      event: 'pagination',
      eventParams: {
        pagination_value: String(page),
      },
    };
    sendIt(analyticsData);
  };

  const onClick = async (e: React.MouseEvent, number: number) => {
    e.preventDefault();
    handleAnalyticData(number);
    await navigate(generateHref(number), { replace: true });
  };

  const paginationProps = {
    totalPages: pager.total_pages,
    currentPage: pager.current_page,
    itemsPerPage: pager.items_per_page,
    totalItems: pager.total_items,
    maxPagesToShow: 5,
    scrollToTop: true,
    zeroBasedIndex: true,
    handlePageClick: onClick,
    ariaLabels: {
      pageItem: (page: number) => `${t('Page')} ${page}`,
      previous: t('Previous Page'),
      next: t('Next Page'),
      current: t('- Current page'),
      ellipsis: t('More pages'),
      first: t('First page'),
      last: t('Last page'),
    },
  };

  return (
    <nav className="[&_a]:pds-no-underline" aria-label={t('Pagination')}>
      <Pagination
        containerRef={containerRef ?? context?.containerRef}
        {...paginationProps}
        // TODO: Remove scale add better responsive styles to PDS-UI.
        className="pds-w-full pds-scale-[0.8] pds-pl-0 md:pds-scale-[1]"
      />
    </nav>
  );
};

export default Pager;
